import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchRegisteredCPAs,
  sendCPAReminder,
} from "../../../api/customAdmin/customAdminService";
import TablaRegisteredCPAs from "../../../components/ui/table/TablaRegisteredCPAs";
import SingleSelect from "../../../components/ui/forms/SingleSelect";
import ModalNew from "../../../components/ui/popup/Modal";
import PopupHeader from "../../../components/ui/popup/common/popupHeader";
import SmallAlert from "../../../components/ui/alerts/SmallAlert";
import SmartButton from "../../../components/ui/buttons/SmartButton";

const RegisteredCPAsSection = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    account_status: "",
    date_joined: "",
    last_reminder: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    ordering: "-custom_user__date_joined",
  });
  const [totalPages, setTotalPages] = useState(1);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedCPA, setSelectedCPA] = useState(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, type: "", message: "" });
  const [modalError, setModalError] = useState("");

  // Mapping of account status to default reminder messages
  const REMINDER_MESSAGES = {
    TN: "Just a quick reminder to continue with the test and finish setting up your Plaintr account. Once completed, you'll be one step closer to accessing the platform.",
    DN: "To continue with your account verification, please submit your professional license when ready. This helps us complete the review process and move your account forward.",
    LR: "Your professional license was reviewed and needs a revision. Please review the feedback provided and resubmit your license when ready so we can continue the verification process.",
    CA: "To start receiving payments through Plaintr, please connect your payout account in your dashboard. This step ensures everything is ready once your account is active.",
    LE: "Please complete your identity verification to activate your Plaintr account. This step is required to ensure compliance and protect everyone on the platform.",
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { ...filters, ...pagination };
      const data = await fetchRegisteredCPAs(params);
      const results = Array.isArray(data?.results) ? data.results : data;
      setRows(results || []);
      if (data?.pagination) {
        setTotalPages(data.pagination.total_pages || 1);
      }
    } catch (e) {
      console.error("Error loading registered CPAs:", e);
      if (e.message.includes("401") || e.message.includes("403")) {
        navigate("/custom-admin", { replace: true });
        return;
      }
      setError("Failed to load registered CPAs");
    } finally {
      setLoading(false);
    }
  }, [navigate, filters, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const updatePagination = (key, value) => {
    setPagination((prev) => ({ ...prev, [key]: value }));
  };

  const clearAllFilters = () => {
    setFilters({
      account_status: "",
      date_joined: "",
      last_reminder: "",
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSendReminder = (cpa) => {
    setSelectedCPA(cpa);
    // Set default message based on account status
    const defaultMessage =
      REMINDER_MESSAGES[cpa.account_status] ||
      "Please complete the pending actions to finish setting up your Plaintr account.";
    setMessage(defaultMessage);
    setModalError("");
    setShowConfirm(true);
  };

  const confirmSendReminder = async () => {
    if (!selectedCPA) return;

    // Clear any previous modal errors
    setModalError("");

    // Validate message is not empty
    if (!message || message.trim() === "") {
      setModalError("Message is required");
      return;
    }

    setSubmitting(true);
    try {
      await sendCPAReminder(selectedCPA.id, {
        message: message.trim(),
      });
      setShowConfirm(false);
      setSelectedCPA(null);
      setMessage("");
      setModalError("");

      // Reload data to update "Last Reminder" column
      await load();

      setToast({
        show: true,
        type: "success",
        message: `Reminder sent successfully to ${selectedCPA.email}`,
      });
    } catch (e) {
      setModalError("Failed to send reminder");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="note info">
        <b>Loading registered CPAs...</b>
      </div>
    );
  if (error)
    return (
      <div className="note error">
        <b>Error: {error}</b>
      </div>
    );

  return (
    <div>
      {/* Filters Section */}
      <div className="form-section admin-logs-filters">
        <div className="admin-logs-filters-header">
          <h4>Filters</h4>
          <button
            className="btn btnSecondary admin-logs-clear"
            onClick={clearAllFilters}
          >
            <b>Clear All Filters</b>
          </button>
        </div>
        <div className="columns col3 admin-logs-filters-grid">
          <div className="select-wrapper">
            <SingleSelect
              optionParam="cpaAccountStatuses"
              name="account_status"
              defaultValue={filters.account_status}
              onChange={(value) => updateFilter("account_status", value)}
              placeholder="Filter By Status"
            />
          </div>
          <div className="inputText">
            <input
              type="date"
              value={filters.date_joined}
              onChange={(e) => updateFilter("date_joined", e.target.value)}
            />
            <label>Date Joined</label>
          </div>
          <div className="inputText">
            <input
              type="date"
              value={filters.last_reminder}
              onChange={(e) => updateFilter("last_reminder", e.target.value)}
            />
            <label>Last Reminder Sent</label>
          </div>
        </div>
      </div>

      <TablaRegisteredCPAs rows={rows} onSendReminder={handleSendReminder} />

      {/* Pagination */}
      <div className="prevNext">
        <div className="select-wrapper page-size-select">
          <label>Page Size</label>
          <SingleSelect
            optionParam="pageSizes"
            name="page_size"
            defaultValue={pagination.page_size.toString()}
            onChange={(value) => updatePagination("page_size", parseInt(value))}
          />
        </div>
        <div className="pagination-controls">
          <button
            className="btn btnSecondary"
            onClick={() =>
              updatePagination("page", Math.max(1, pagination.page - 1))
            }
            disabled={pagination.page <= 1}
          >
            <b>Previous</b>
          </button>
          <span className="page-info">
            Page {pagination.page} of {totalPages}
          </span>
          <button
            className="btn btnSecondary"
            onClick={() =>
              updatePagination(
                "page",
                Math.min(totalPages, pagination.page + 1)
              )
            }
            disabled={pagination.page >= totalPages}
          >
            <b>Next</b>
          </button>
        </div>
      </div>

      {/* Send Reminder Modal */}
      {showConfirm && (
        <ModalNew>
          <PopupHeader title="Send Reminder" setOpenState={setShowConfirm} />
          <div className="validate-license-modal-body">
            {modalError && (
              <div className="modal-alert-container">
                <SmallAlert alert="True" type="error" message={modalError} />
              </div>
            )}

            <div className="mb-16">
              <h4>CPA Information:</h4>
              <p>
                <strong>Name:</strong> {selectedCPA?.first_name}{" "}
                {selectedCPA?.last_name}
              </p>
              <p>
                <strong>Email:</strong> {selectedCPA?.email}
              </p>
              <p>
                <strong>Status:</strong> {selectedCPA?.account_status_display}
              </p>
            </div>

            <div className="inputText validate-license-inputText-spacing">
              <textarea
                rows="4"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={submitting}
                maxLength={500}
              />
              <label>Message (required)</label>
              <small className="character-count">
                {message.length}/500 characters
              </small>
            </div>

            <div className="validate-license-actions">
              <SmartButton
                text="Send Reminder"
                icon="mail"
                onClick={confirmSendReminder}
                className="btnAccept"
                disabled={submitting}
                isSubmitting={submitting}
                loadingText="Sending..."
              />
            </div>
          </div>
        </ModalNew>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div className="validate-license-toast">
          <SmallAlert
            alert="True"
            title={toast.type === "success" ? "Success" : "Error"}
            type={toast.type}
            message={toast.message}
            dismissOnClickOutside={true}
            dismissOnFocusOutside={false}
            onDismiss={() => setToast({ show: false, type: "", message: "" })}
          />
        </div>
      )}
    </div>
  );
};

export default RegisteredCPAsSection;
